// =============================================================
// exportDownload.js — bulk export helpers shared by the Documents page, the
// deal/deal-detail Documents tab, and the notes feed.
//
// Two exports, both built the same way: the API assembles the bundle
// server-side and hands back a short-lived read SAS URL (the archive/PDF never
// rides through the JSON response), and the browser saves it.
//
//   downloadDocumentsZip()   → POST /api/document-download-zip           (.zip of a scope's files)
//   exportTransactionsXlsx() → POST /api/deals/{id}/details/{did}/transactions/export  (.xlsx ledger)
//   exportCashFlowXlsx()     → POST /api/reports/cash-flow-projection/export  (.xlsx: matrix, flows, rates)
//   exportNotesPdf()         → POST /api/notes/export-pdf                (.pdf of a scope's notes)
//   exportConversationPdf()  → POST /api/conversations/{id}/export-pdf   (.pdf of a chat thread)
//
// Both are slow by nature — the server reads every blob / renders every note —
// so each passes a generous per-request timeout instead of the client default.
// =============================================================

import { api } from '../api/client';

const BUNDLE_TIMEOUT_MS = 240000;   // 4 min — reading and zipping many blobs
const PDF_TIMEOUT_MS    = 120000;   // 2 min — rendering a long notes PDF
const XLSX_TIMEOUT_MS   = 120000;   // 2 min — a full ledger, not the 200 on screen
// 5 min — the whole book projected, every ledger read, then a workbook written.
const CASHFLOW_XLSX_TIMEOUT_MS = 300000;

// Hand a URL to the browser as a download. The SAS URL already carries
// `Content-Disposition: attachment`, which is what actually forces the save —
// the `download` attribute is ignored cross-origin.
function saveUrl(url, fileName) {
  const a = document.createElement('a');
  a.href = url;
  if (fileName) a.download = fileName;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

// Fallback path for when the API had no Blob Storage to stage the file in
// (local dev) and inlined the bytes instead.
function saveBase64(base64, fileName, mime) {
  const bin = atob(base64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  const url = URL.createObjectURL(new Blob([bytes], { type: mime }));
  saveUrl(url, fileName);
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

/**
 * Download every document in a deal / deal-detail scope as one .zip.
 *
 * @param {object}   scope
 * @param {string}  [scope.dealId]        deal scope
 * @param {string}  [scope.dealDetailId]  deal-detail scope (one of the two is required)
 * @param {string}  [scope.folderPath]    narrow to this folder and everything under it
 * @param {boolean} [scope.includeDealLevel] with a detail scope, also include the deal-level files
 * @param {string[]} [scope.documentIds]  narrow to an explicit selection within the scope
 * @returns {Promise<{fileName, fileCount, totalBytes, skipped}>}
 */
export async function downloadDocumentsZip(scope) {
  const res = await api.post('document-download-zip', {
    dealId:           scope.dealId       || null,
    dealDetailId:     scope.dealDetailId || null,
    folderPath:       scope.folderPath   || null,
    includeDealLevel: !!scope.includeDealLevel,
    ...(scope.documentIds?.length ? { documentIds: scope.documentIds } : {}),
  }, { timeout: BUNDLE_TIMEOUT_MS });

  if (res?.sasUrl) saveUrl(res.sasUrl, res.fileName);
  return res;
}

/**
 * Export a scope's notes to a PDF and save it.
 *
 * @param {object}  scope
 * @param {string} [scope.taskId]              task-scoped notes
 * @param {string} [scope.dealId]              deal / deal-detail notes
 * @param {string} [scope.dealDetailId]
 * @param {boolean}[scope.includeDealLevel]    merge deal-level notes into a detail export
 * @param {boolean}[scope.includeSummary]      prepend the AI summary (default true)
 * @param {boolean}[scope.includeAttachments]  list each note's attachments (default true)
 * @returns {Promise<{fileName, noteCount, truncated}>}
 */
export async function exportNotesPdf(scope) {
  const res = await api.post('notes/export-pdf', {
    taskId:             scope.taskId       || null,
    dealId:             scope.dealId       || null,
    dealDetailId:       scope.dealDetailId || null,
    includeDealLevel:   !!scope.includeDealLevel,
    includeSummary:     scope.includeSummary     !== false,
    includeAttachments: scope.includeAttachments !== false,
  }, { timeout: PDF_TIMEOUT_MS });

  if (res?.sasUrl)         saveUrl(res.sasUrl, res.fileName);
  else if (res?.pdfBase64) saveBase64(res.pdfBase64, res.fileName, 'application/pdf');
  return res;
}

/**
 * Export a shared chat thread to a PDF and save it.
 *
 * The transcript is rendered server-side rather than printed from the DOM
 * because the value of an answer is often the answer PLUS its provenance, and
 * the document lookups behind it live in tool blocks a print stylesheet drops.
 *
 * @param {string}  conversationId
 * @param {object} [opts]
 * @param {boolean}[opts.includeSources]  list the documents each answer searched (default true)
 * @param {boolean}[opts.includeVersions] include superseded turns (default false)
 * @returns {Promise<{fileName, messages, truncated}>}
 */
export async function exportConversationPdf(conversationId, opts = {}) {
  const res = await api.post(`conversations/${conversationId}/export-pdf`, {
    includeSources:  opts.includeSources  !== false,
    includeVersions: opts.includeVersions === true,
  }, { timeout: PDF_TIMEOUT_MS });

  if (res?.sasUrl)         saveUrl(res.sasUrl, res.fileName);
  else if (res?.pdfBase64) saveBase64(res.pdfBase64, res.fileName, 'application/pdf');
  return res;
}

/** "3 files (2.1 MB)" — the confirmation line shown after a bundle download. */
export function describeBundle(res) {
  if (!res) return '';
  const mb = res.totalBytes ? ` (${(res.totalBytes / (1024 * 1024)).toFixed(1)} MB)` : '';
  const n  = res.fileCount ?? 0;
  const skipped = res.skipped?.length ? ` — ${res.skipped.length} skipped, see _MANIFEST.txt` : '';
  return `Downloaded ${n} file${n === 1 ? '' : 's'}${mb}${skipped}`;
}

/**
 * Export a deal detail's Sage transactions to a .xlsx and save it.
 *
 * THE FILE IS NOT WHAT IS ON SCREEN. The tab renders the 200 most recent
 * postings; the export runs its own query over the whole ledger, so the two
 * legitimately differ in length — which is the point of the button. It matches
 * on every other axis (same source, same interest exclusion, same grouping),
 * so nothing appears in one and not the other for any reason but the cap.
 *
 * @param {string} dealId
 * @param {string} dealDetailId
 * @returns {Promise<{fileName, rowCount, truncated, maxRows}>}
 */
/**
 * THE CASH FLOW REPORT'S OWN ARITHMETIC, as a workbook — the dated flows every
 * figure on that report was solved over, plus the terminals, the per-deal index
 * comparison and each rate with its duration and multiple.
 *
 * IT REPLACED A CLIENT-SIDE CSV OF THE MATRIX, which is now a sheet in this
 * workbook with formula totals. So there is one export rather than two files to
 * reconcile — and it asks the server to re-run the SAME report with its
 * underlying rows attached, so the file carries the flows the screen never
 * shows. The caller passes the filters that reached the server plus the
 * measure, or the file would describe a different run from the one on screen;
 * the search box is not among them and the file is the whole run.
 *
 * A long timeout: the server projects every deal in the book, reads the whole
 * ledger and writes a workbook. The 10s client default would report a timeout
 * over an export that landed (the SageImport.jsx lesson).
 */
export async function exportCashFlowXlsx(filters = {}) {
  const res = await api.post('reports/cash-flow-projection/export', {
    granularity: filters.granularity || undefined,
    status: filters.status && filters.status !== 'All' ? filters.status : undefined,
    from: filters.from || undefined,
    to: filters.to || undefined,
    dealId: filters.dealId || undefined,
    // The capital-type / hold-type narrowing the screen was run with, as
    // arrays; null or empty means the whole book and is not sent. The API
    // refuses an unknown capital type by name, exactly as the report does.
    detailTypes: Array.isArray(filters.detailTypes) && filters.detailTypes.length ? filters.detailTypes : undefined,
    holdTypes: Array.isArray(filters.holdTypes) && filters.holdTypes.length ? filters.holdTypes : undefined,
    // The vintage cutoff (YYYY-MM-DD) the screen was run with — a cohort
    // filter, so a file without it would cover different deals from the screen.
    deployedFrom: filters.deployedFrom || undefined,
    // Which series fills the Matrix sheet. Client-side on the screen, but the
    // file has to be written at the measure the table was read at or the two
    // disagree; the API normalizes anything it does not recognise and the
    // workbook's README names the one it used.
    measure: filters.measure || undefined,
    // Only an explicit false opts out, matching the API and the report.
    ...(filters.includeActuals === false ? { includeActuals: false } : {}),
    // The deal-level Sage entities, the same shape: an older page that never
    // sends the field keeps the holding companies' money IN.
    ...(filters.includeEntities === false ? { includeEntities: false } : {}),
    // Which OWNER entities the screen was narrowed to: the codes off the
    // report's own ownerOptions (plus 'none' for deals under no entity), as
    // an array. Null or empty means every deal and is not sent.
    ownerEntities: Array.isArray(filters.ownerEntities) && filters.ownerEntities.length
      ? filters.ownerEntities : undefined,
  }, { timeout: CASHFLOW_XLSX_TIMEOUT_MS });
  if (res?.sasUrl) saveUrl(res.sasUrl, res.fileName);
  else if (res?.fileBase64) {
    saveBase64(res.fileBase64, res.fileName,
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
  } else throw new Error('The export produced no file.');
  return res;
}

// Hand a built .xlsx response ({ sasUrl | fileBase64, fileName }) to the
// browser. Used by the Harvi workbook card, which calls its own route.
export function saveXlsxResult(res) {
  if (res?.sasUrl) saveUrl(res.sasUrl, res.fileName);
  else if (res?.fileBase64) {
    saveBase64(res.fileBase64, res.fileName,
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
  } else throw new Error('The workbook produced no file.');
  return res;
}

// A Harvi document draft, built server-side as Word or PDF.
const DOCUMENT_MIME = {
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  pdf: 'application/pdf',
};
export function saveDocumentResult(res) {
  if (res?.sasUrl) saveUrl(res.sasUrl, res.fileName);
  else if (res?.fileBase64) saveBase64(res.fileBase64, res.fileName, DOCUMENT_MIME[res.format] || DOCUMENT_MIME.docx);
  else throw new Error('The document produced no file.');
  return res;
}

export async function exportTransactionsXlsx(dealId, dealDetailId) {
  const res = await api.post(
    `deals/${dealId}/details/${dealDetailId}/transactions/export`,
    {},
    { timeout: XLSX_TIMEOUT_MS },
  );
  if (res?.sasUrl) saveUrl(res.sasUrl, res.fileName);
  else if (res?.fileBase64) {
    saveBase64(res.fileBase64, res.fileName,
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
  }
  return res;
}

// The Cash Flow Projection's EVENT layer as a .xlsx — every individual dated
// cash flow, and the only export that report has.
//
// SERVER-SIDE BECAUSE THE EVENTS ARE NOT HERE. The report payload carries each
// capital item's `eventCount`/`firstDate`/`lastDate` and not the flows
// themselves (cashFlowProjection.js keeps that list opt-in so the page does not
// fetch a per-flow array on every navigation), so this cannot be built from
// what the client holds.
//
// THE FILTERS ARE PASSED, NOT THE ROWS. The server re-runs the same loader with
// the same scope, so the file covers exactly what the caller's own report run
// covers. Note the on-screen SEARCH BOX and "Hide 0 rows" are NOT sent: those
// narrow the rendered table client-side and have no server equivalent, so the
// workbook is the whole scope — which is what somebody exporting the raw layer
// wants, and the Report sheet states the filters that WERE applied. The MEASURE
// toggle is not sent either, and cannot be: an event is a signed amount, so the
// file carries inflow, outflow and net at once rather than one of them.
//
// A long timeout for the XLSX_TIMEOUT_MS reason: the run reads every source
// table in the book and writes a blob, and the client's 10 s default would
// report a timeout over an export that landed.
export async function exportCashFlowEventsXlsx(filters = {}) {
  const res = await api.post(
    'reports/cash-flow-projection/export',
    {
      granularity: filters.granularity || undefined,
      from: filters.from || undefined,
      to: filters.to || undefined,
      status: filters.status || undefined,
      dealId: filters.dealId || undefined,
      // Only an explicit false is sent — an absent flag leaves the ledger in,
      // matching the server's own default (a typo must not hide money).
      ...(filters.includeActuals === false ? { includeActuals: false } : {}),
    },
    { timeout: XLSX_TIMEOUT_MS },
  );
  if (res?.sasUrl) saveUrl(res.sasUrl, res.fileName);
  else if (res?.fileBase64) {
    saveBase64(res.fileBase64, res.fileName,
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
  }
  return res;
}

/** One line describing what the export actually delivered, truncation included. */
/**
 * What the live workbook actually contained, for the banner on the report.
 *
 * THE FORMULA COUNT IS REPORTED because a workbook whose arithmetic is live is
 * indistinguishable, from the outside, from one carrying the same figures as
 * dead values — and a release that silently stopped emitting them would look
 * exactly like a working one. The route counts what it placed; this says so.
 *
 * THE CAP BITES THE FLOWS SHEET ONLY. Every other sheet is computed over the
 * whole run, so on a truncated export they will NOT tie to the rows — which is
 * why that is said here and on the README rather than left to be discovered.
 */
export function describeCashFlowWorkbook(res) {
  if (!res) return '';
  const n = Number(res.rowCount || 0).toLocaleString();
  const f = Number(res.formulaCount || 0);
  const formulas = f ? ` ${f.toLocaleString()} cells recalculate.` : '';
  if (res.truncated) {
    const cap = res.maxRows ? Number(res.maxRows).toLocaleString() : n;
    return `Exported the first ${n} cash flows (the ${cap} row cap). `
      + 'Every other sheet is computed over the whole run, so it will not tie to the rows. '
      + `Narrow it with a date window for the whole set.${formulas}`;
  }
  const sheets = Array.isArray(res.sheets) && res.sheets.length
    ? ` across ${res.sheets.length} sheets` : '';
  // WHICH KIND OF CHART LANDED. A live chart, the figures only (with the
  // API's reason), or nothing to draw — said here because a missing drawing is
  // otherwise discovered in Excel, where nothing explains it.
  const chart = res.chart === 'native'
    ? ' The growth chart is on the Chart sheet as a live chart.'
    : res.chart === 'data-only'
      ? ` The Chart sheet carries the growth chart’s figures only${res.chartReason ? ` — ${res.chartReason}` : '.'}`
      : '';
  return `Exported ${n} cash flows${sheets}.${formulas}${chart}`;
}

export function describeCashFlowExport(res) {
  if (!res) return '';
  const n = Number(res.rowCount || 0).toLocaleString();
  const items = Number(res.itemCount || 0).toLocaleString();
  if (res.truncated) {
    const total = Number(res.eventTotal || 0).toLocaleString();
    // THE CAP BITES THE CASH FLOWS SHEET ONLY. The grid and the per-item totals
    // are computed from the whole report, so they stay complete and will not
    // tie to the truncated rows — which is exactly why that is said here and on
    // the Report sheet rather than left to be noticed.
    return `Exported the first ${n} of ${total} cash flows (the ${res.maxRows?.toLocaleString?.() || res.maxRows} row cap). `
      + 'The Period Grid and Capital Items sheets still cover every flow, so they will not tie to the rows. '
      + 'Narrow it with a deal or a date window for the whole set.';
  }
  // The grid's row count is items x the four figures, so it is worth stating
  // rather than leaving a reader to wonder why that sheet is four times longer.
  const grid = res.gridRowCount ? `, and a ${Number(res.gridRowCount).toLocaleString()}-row period grid` : '';
  return `Exported ${n} cash flows across ${items} capital items${grid}.`;
}
